/**
 * @module utils/emitter
 */

/**
 * @typedef {*} Value
 */

/**
 * @typedef {function} Listen
 * @returns {AsyncGenerator<Value>}
 * @throws {Error} Thrown if the emitter is already closed
 */

/**
 * @typedef {function} Emit
 * @param {Value} value
 */

/**
 * @typedef {{}} Emitter
 * @property {Promise<void>} closed
 * @property {Emit} emit
 * @property {Listen} listen
 */

import object from './object.js'

const done = true
const symbol = Symbol()
const value = undefined
const writable = function * (listeners) {
  while (true) {
    const { promise, resolve } = Promise.withResolvers()

    listeners[0] = promise

    for (const [key, reader] of listeners.entries()) {
      if (key) {
        reader.push(promise)
      }
    }

    resolve(yield)
  }
}

const readable = async function * (listeners, closed) {
  const promises = listeners.at(-1)

  while (true) {
    const value = await Promise.race([promises.shift(), closed])

    if (value === symbol) {
      break
    }

    yield value
  }
}

/**
 * Returns an emitter object to create some async listeners
 * of any values emitted on the provided emitter
 * @param {Boolean|true} autoClose
 * @returns {Emitter}
 */
export default (
  autoClose = true
) => {
  const { promise: closed, resolve: close } = Promise.withResolvers()
  const listeners = []
  const emitter = writable(listeners, close)
  let closing

  emitter.next()

  return object(null, {
    closed,
    emit: value => {
      if (!closing) {
        emitter.next(value)
      }
    },
    listen: () => {
      if (closing) {
        throw new Error('Already closed emitter')
      }

      const promises = [listeners.at(0)]

      listeners.push(promises)

      const listener = readable(listeners, closed)
      const { return: fn } = listener

      listener.return = async () => {
        if (autoClose && listeners.length === 2) {
          closing = true
        }

        queueMicrotask(async () => {
          const index = listeners.indexOf(promises)

          if (index !== -1) {
            listeners.splice(index, 1)

            if (closing) {
              await emitter.return()
              close(symbol)
            }
          }
        })

        await fn.call(listener)
        
        return { done, value }
      }

      return listener
    }
  })
}
